import { useMemo } from 'react'
import { isEmpty } from 'lodash'
import { MARK_TYPE } from 'config/constants/preview'
import { HOTKEYS } from 'enums'
import { useHotkeys } from 'react-hotkeys-hook'
import { DEFAULT_IMAGE_DURATION } from 'constant'
import { AudioAsset } from 'models/Asset'
import { usePlayingTimelineFragment } from 'hooks/timeline/iopoints/usePlayingTimelineFragment'
import { timelineTimeToSeconds } from 'Util'
import { useEditClip } from './useEditClip'
import { UseClipCreator } from './types'
import { usePlayerProgress } from './usePlayerProgress'
import { useContextMenu } from './useContextMenu'
import { useIOMarkers } from './useIOMarkers'
import { useInitialData } from './useInitialData'
import { useIOPointsTimelineAssetAsSourcePreviewClip } from './useIOPointsTimelineAssetAsSourcePreviewClip'
import { usePlayingSourceMediaFragment } from '../sourceMedia/usePalyingSourceMediaFragment'
import { usePreviewSlider } from '../usePreviewSlider'

export const useClipCreator = ({
  preview,
  progress,
  previewPlayerVideoElem,
  duration,
  currentFps,
  asset,
  onRewind,
  isMedia,
  onTogglePlayPause,
}: UseClipCreator) => {
  const {
    dispatch,
    mouseMovementData,
    timeout,
    activeHotkeyProfile,
    activePreview,
    showInOutPoints,
    inOutPointsTimeLineAsset,
    minClipDuration,
    getTimePointToPercent,
    layerAssets,
    setDragging,
    isDragging,
    ioPointsBarRef,
    isResize,
    isMediaServerProcessing,
    isStaticAsset,
    memoSliderTime,
    inOutPointsAssetDissolve,
  } = useInitialData({ currentFps, asset, isMedia })
  const { sliderContainerRef } = usePreviewSlider({ isMedia, preview })
  const { onCloseMenu, onOpenMenu, anchorEl, menuCoords } = useContextMenu({ sliderContainerRef })

  const {
    clipCreatorMarkers,
    handleSetLocalMarkers,
    localMarkers,
    setLocalMarkers,
    onClearInOutPoints,
    onSetMarker,
    onClickMarker,
  } = useIOMarkers({
    progress,
    minClipDuration,
    duration,
    showInOutPoints,
    activePreview,
    mouseMovementData,
    dispatch,
    inOutPointsTimeLineAsset,
    onRewind,
    layerAssets,
    preview,
    isDragging,
    assetID: asset?.id,
    asset,
    isStaticAsset,
    isMedia,
    inOutPointsAssetDissolve,
  })

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.MARK_IN], onClickMarker(preview, MARK_TYPE.IN))
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.MARK_OUT], onClickMarker(preview, MARK_TYPE.OUT))
  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.CLEAR_IN_OUT_MARK_POINTS], onClearInOutPoints())

  const clipMarkers = !isEmpty(localMarkers)
    ? localMarkers[preview]
    : clipCreatorMarkers[preview]
  const timeCodeMarkers = useMemo(() => ({
    [MARK_TYPE.IN]: clipMarkers.markIn || 0,
    [MARK_TYPE.OUT]: clipMarkers.markOut || (clipMarkers.markIn ? duration : 0),
  }), [ clipMarkers, duration ])

  let rightTimeCodeDuration = !clipMarkers.markIn && !clipMarkers.markOut
    ? duration
    : timeCodeMarkers[MARK_TYPE.OUT] - timeCodeMarkers[MARK_TYPE.IN]

  if (!isMedia) {
    if ((timeCodeMarkers[MARK_TYPE.OUT] > duration) || (timeCodeMarkers[MARK_TYPE.IN] > duration)) {
      rightTimeCodeDuration = duration
    }
  }

  if (isMedia) {
    if (!timeCodeMarkers[MARK_TYPE.OUT] && !timeCodeMarkers[MARK_TYPE.IN]) {
      if (isStaticAsset) {
        rightTimeCodeDuration = timelineTimeToSeconds(DEFAULT_IMAGE_DURATION)
      }
      if (asset instanceof AudioAsset) {
        rightTimeCodeDuration = duration
      }
    }
  }

  const { onClickMarkIcon, onSetDragging, savedProgress } = useEditClip({
    preview,
    activePreview,
    onSetMarker,
    clipCreatorMarkers,
    previewPlayerVideoElem,
    duration,
    onRewind,
    progress,
    mouseMovementData,
    handleSetLocalMarkers,
    setLocalMarkers,
    localMarkers,
    asset,
    setDragging,
    ioPointsBarRef,
    isMediaServerProcessing,
    isDragging,
    isMedia,
  })

  usePlayerProgress({
    preview,
    onRewind,
    progress: memoSliderTime ?? savedProgress ?? progress,
    timeout,
    isMediaServerProcessing,
    isStaticAsset,
  })

  useIOPointsTimelineAssetAsSourcePreviewClip({
    onRewind,
    preview,
    inOutPointsTimeLineAsset,
    timeCodeMarkers,
    mouseMovementData,
    activePreview,
  })

  usePlayingSourceMediaFragment({
    seek: onRewind,
    duration,
    progress,
    onTogglePlayPause,
    preview,
  })

  usePlayingTimelineFragment({ preview })

  return {
    onSetMarker,
    timeCodeMarkers,
    getTimePointToPercent,
    onClearInOutPoints,
    sliderContainerRef,
    onOpenMenu,
    onCloseMenu,
    anchorEl,
    anchorPosition: menuCoords,
    onClickMarkIcon,
    savedProgress,
    showInOutPoints,
    clipMarkers,
    isDragging,
    onSetDragging,
    isResize,
    onClickMarker,
    ioPointsBarRef,
    sliderProgress: memoSliderTime ?? savedProgress ?? progress,
    rightTimeCodeDuration,
  }
}
